import { EnvironmentObject } from '@rapid-recovery-agency-inc/insightt-shared';

export const LOCAL_ENVIRONMENT: EnvironmentObject = {
  BACKEND_SERVER: import.meta.env.VITE_BACKEND_SERVER,
  AWS_CLOUDWATCH_APPLICATION_ID: import.meta.env.VITE_AWS_CLOUDWATCH_APPLICATION_ID,
  AWS_CLOUDWATCH_IDENTITY_POOL_ID: import.meta.env.VITE_AWS_CLOUDWATCH_IDENTITY_POOL_ID,
  DEBUG: import.meta.env.VITE_DEBUG === 'True',
  ENABLE_MONITORING: String(import.meta.env.VITE_ENABLE_MONITORING) === 'True',
  ENABLE_PERSISTED_QUERIES: import.meta.env.VITE_ENABLE_PERSISTED_QUERIES === 'True',
  ENVIRONMENT: import.meta.env.VITE_ENVIRONMENT,
  GOOGLE_API_KEY: import.meta.env.VITE_GOOGLE_API_KEY,
  MIXPANEL_KEY: import.meta.env.VITE_MIXPANEL_KEY,
  CLEVERTAP_ACCOUNT: import.meta.env.VITE_CLEVERTAP_ACCOUNT,
  CLEVERTAP_TOKEN: import.meta.env.VITE_CLEVERTAP_TOKEN,
  SEGMENT_KEY: import.meta.env.VITE_SEGMENT_KEY,
  STRIPE_PUBLIC_KEY: import.meta.env.VITE_STRIPE_PUBLIC_KEY,
  GAMIFICATION_APP_URL: import.meta.env.VITE_GAMIFICATION_APP_URL,
};

/**
 * Build-time SSO settings (#3888). They are not part of `LOCAL_ENVIRONMENT`: `EnvironmentObject` is
 * owned by `insightt-shared`, and the environment switcher can replace it at runtime, while these
 * values must always come from the running build.
 */
export interface SsoEnvironment {
  SSO_BASE_URL?: string;
  SSO_CLIENT_ID?: string;
  SSO_CALLBACK_URI?: string;
  SSO_SILENT_CALLBACK_URI?: string;
  SSO_POST_LOGOUT_URI?: string;
  APP_BASE_URL: string;
}

type SsoSettingKey = Exclude<keyof SsoEnvironment, 'APP_BASE_URL'>;

/** The Vite variable behind each SSO setting, used in diagnostics and the env contract test. */
export const SSO_ENV_VAR_NAMES: Readonly<Record<SsoSettingKey, string>> = {
  SSO_BASE_URL: 'VITE_SSO_BASE_URL',
  SSO_CLIENT_ID: 'VITE_SSO_CLIENT_ID',
  SSO_CALLBACK_URI: 'VITE_SSO_CALLBACK_URI',
  SSO_SILENT_CALLBACK_URI: 'VITE_SSO_SILENT_CALLBACK_URI',
  SSO_POST_LOGOUT_URI: 'VITE_SSO_POST_LOGOUT_URI',
};

const SSO_ENVIRONMENT_READERS: { [K in keyof SsoEnvironment]: () => SsoEnvironment[K] } = {
  SSO_BASE_URL: () => import.meta.env.VITE_SSO_BASE_URL,
  SSO_CLIENT_ID: () => import.meta.env.VITE_SSO_CLIENT_ID,
  SSO_CALLBACK_URI: () => import.meta.env.VITE_SSO_CALLBACK_URI,
  SSO_SILENT_CALLBACK_URI: () => import.meta.env.VITE_SSO_SILENT_CALLBACK_URI,
  SSO_POST_LOGOUT_URI: () => import.meta.env.VITE_SSO_POST_LOGOUT_URI,
  APP_BASE_URL: () => import.meta.env.BASE_URL,
};

/**
 * Adds the SSO settings to an environment object as read-only, non-enumerable getters. They are
 * read on access, so tests can stub them, and stay out of the JSON the environment switcher saves.
 */
const withSsoEnvironment = <T extends object>(env: T): T & SsoEnvironment => {
  const target = { ...env };
  (Object.keys(SSO_ENVIRONMENT_READERS) as Array<keyof SsoEnvironment>).forEach((key) => {
    Object.defineProperty(target, key, { get: SSO_ENVIRONMENT_READERS[key], enumerable: false });
  });
  return target as T & SsoEnvironment;
};

/**
 * The `btoa` function in JavaScript stands for "binary to ASCII" and is used to encode a string
 * in base64. It takes a string as input and returns a base64-encoded ASCII string. This function
 * is commonly used to encode sensitive information or data before storing it in localStorage or
 * transmitting it over the network.
 */
const btoa = (payload: string) => {
  return window.btoa(payload);
};

/**
 * The `atob` function in JavaScript stands for "ASCII to binary" and is used to decode a
 * base64-encoded ASCII string back to its original form. It takes a base64-encoded string as
 * input and returns the decoded original string. This function is commonly used to decode
 * information or data that was previously encoded using the `btoa` function before storing it in
 * localStorage or transmitting it over the network.
 */
const atob = (payload: string) => {
  return window.atob(payload);
};

/**
 * The lsSetEnvObj function takes an EnvironmentObject as input and stores it in the localStorage after
 * encoding it to base64.
 * @param {EnvironmentObject} envObj - The `envObj` parameter in the `lsSetEnvObj` function is of type
 * `EnvironmentObject`. It is an object that represents environment settings or configurations.
 */
const lsSetEnvObj = (envObj: EnvironmentObject) => {
  localStorage.setItem('envObj', btoa(JSON.stringify(envObj)));
};

/**
 * The function `localEnvironmentValidator` retrieves environment settings from localStorage, decodes
 * and parses them, and returns a merged object with default values if necessary.
 * @returns The `localEnvironmentValidator` function is returning an object of type
 * `EnvironmentObject`. This object is created by iterating over the keys of `LOCAL_ENVIRONMENT`,
 * checking if the corresponding value is falsy. If the value is falsy, it is replaced with the value
 * from the parsed `envObj`. The final object is then returned as `EnvironmentObject`.
 */
const buildLocalEnvironment = () => {
  // Get the localStorage environment and parse it
  const lsObj = localStorage.getItem('envObj');
  const envObj: EnvironmentObject = lsObj ? JSON.parse(atob(lsObj)) : LOCAL_ENVIRONMENT;
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  const returnObj: any = {};
  const localEnvObjKeys = Object.keys(LOCAL_ENVIRONMENT) as Array<keyof EnvironmentObject>;
  localEnvObjKeys.forEach((k) => {
    const value = LOCAL_ENVIRONMENT[k];
    const newValue = envObj[k];

    // if an LOCAL_ENVIRONMENT value is undefined or ''
    // undefined because some values of LOCAL_ENVIRONMENT are booleans or numbers
    if (value !== undefined || value === '') {
      returnObj[k] = value;
    } else {
      returnObj[k] = newValue;
    }
  });

  return returnObj as EnvironmentObject;
};

/**
 * The function `buildRemoteEnvironment` takes an input object and returns a modified version with
 * specific boolean properties based on string values.
 * @param {any} input - The `buildRemoteEnvironment` function takes an `input` object as a parameter
 * and returns an `EnvironmentObject`.
 * @returns The returned object includes the properties from the input object, but
 * the values of the properties `ENABLE_MONITORING`, `DEBUG`, and `ENABLE_PERSISTED_QUERIES` are
 * converted to boolean values based on whether they are equal to the string 'True' in the input object
 */
// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const buildRemoteEnvironment = (input: any): EnvironmentObject => {
  return {
    ...input,
    ENABLE_MONITORING: input.ENABLE_MONITORING === 'True',
    DEBUG: input.DEBUG === 'True',
    ENABLE_PERSISTED_QUERIES: input.ENABLE_PERSISTED_QUERIES === 'True',
  };
};

/**
 * The function `lsGetEnvVariables` retrieves environment variables from localStorage or a local
 * environment validator.
 * @returns `{
 *   localEnvKey: LOCAL_ENVIRONMENT.ENVIRONMENT,
 *   envObj: lsObj ? JSON.parse(atob(lsObj)) : localEnvironmentValidator(),
 * }`
 */
const lsGetEnvVariables = () => {
  const lsObj = localStorage.getItem('envObj');
  const envObj: EnvironmentObject = lsObj ? JSON.parse(atob(lsObj)) : buildLocalEnvironment();
  return {
    envObj,
  };
};

/**
 * The `refreshEnv` function updates the environment object and reloads the window if a valid object is
 * provided, otherwise it sets a valid local environment object and reloads the window.
 * type `EnvironmentObject | null`. This means it can either be an object of type `EnvironmentObject`
 * or `null`.
 * @returns In the `refreshEnv` function, if `envObj` is not null, the function sets the environment
 * object using `lsSetEnvObj(envObj)` and then reloads the window using `window.location.reload()`. No
 * explicit return value is provided in this case.
 * @param data
 */
const refreshEnv = (data: EnvironmentObject | null) => {
  if (!data) {
    const validLocalEnv = buildLocalEnvironment();
    lsSetEnvObj(validLocalEnv);
    window.location.reload();
    return;
  }

  const envObj = buildRemoteEnvironment(data);
  lsSetEnvObj(envObj);
  window.location.reload();
};

/** `setupEnv` is a function that is responsible for setting up the environment variables based on
 * the data stored in the localStorage. It reads the environment key, environment object, and
 * environment object list from the localStorage.
 * @returns `EnvironmentObject`
 */
const setupEnv = () => {
  // Reads envObj from localStorage
  const { envObj } = lsGetEnvVariables();

  // Default condition when an env is already setted up and it's not 'local.env'
  if (envObj && LOCAL_ENVIRONMENT.ENVIRONMENT !== envObj.ENVIRONMENT) {
    return envObj;
  }

  const validLocalEnv = buildLocalEnvironment();

  // Condition when there isn't a envObj or its name is the same as `LOCAL_ENVIRONMENT`
  lsSetEnvObj(validLocalEnv);
  return LOCAL_ENVIRONMENT;
};

export { lsGetEnvVariables, setupEnv, refreshEnv, withSsoEnvironment };
